import type { Observable } from 'rxjs';

import type { PageQuery, PagedResult } from '@domain/models/paged-result.model';
import type {
  ActiveQuestion,
  AgoraToken,
  CreateStreamSessionResult,
  LeaderboardEntry,
  OpenQuestionResult,
  QuizQuestion,
  QuizQuestionDraft,
  StreamPrizeTier,
  StreamQuestion,
  StreamQuestionResults,
  StreamSession,
} from '@domain/models/streaming.model';

export abstract class StreamingRepository {
  abstract listSessions(): Observable<readonly StreamSession[]>;
  abstract createSession(title: string): Observable<CreateStreamSessionResult>;
  abstract startSession(streamSessionId: number): Observable<void>;
  abstract endSession(streamSessionId: number): Observable<void>;
  abstract getAgoraToken(streamSessionId: number): Observable<AgoraToken>;

  abstract listStreamQuestions(streamSessionId: number): Observable<readonly StreamQuestion[]>;
  abstract attachQuestion(
    streamSessionId: number,
    questionId: number,
  ): Observable<{ streamQuestionId: number }>;
  abstract openQuestion(
    streamSessionId: number,
    streamQuestionId: number,
  ): Observable<OpenQuestionResult>;
  abstract closeQuestion(streamSessionId: number, streamQuestionId: number): Observable<void>;
  abstract getActiveQuestion(streamSessionId: number): Observable<ActiveQuestion | null>;
  abstract getQuestionResults(
    streamSessionId: number,
    streamQuestionId: number,
  ): Observable<StreamQuestionResults>;
  abstract getLeaderboard(
    streamSessionId: number,
    query?: PageQuery,
  ): Observable<PagedResult<LeaderboardEntry>>;
  abstract getPrizeTiers(streamSessionId: number): Observable<readonly StreamPrizeTier[]>;
  abstract setPrizeTiers(
    streamSessionId: number,
    tiers: readonly StreamPrizeTier[],
  ): Observable<void>;
}

export abstract class QuizQuestionRepository {
  abstract list(query: PageQuery): Observable<PagedResult<QuizQuestion>>;
  abstract getById(questionId: number): Observable<QuizQuestion>;
  abstract create(draft: QuizQuestionDraft): Observable<{ questionId: number }>;
  abstract update(questionId: number, draft: QuizQuestionDraft): Observable<void>;
  abstract delete(questionId: number): Observable<void>;
}
